/**
 * Twitter/X Feed Post Mockup
 * Simulates how an ad appears in the Twitter/X feed
 */

import React from 'react';
import { MessageCircle, Repeat2, Heart, BarChart2, Share, MoreHorizontal } from 'lucide-react';

interface TwitterFeedMockupProps {
  children: React.ReactNode;
  adWidth: number;
  adHeight: number;
}

export function TwitterFeedMockup({ children, adWidth, adHeight }: TwitterFeedMockupProps) {
  return (
    <div className="bg-black rounded-xl overflow-hidden shadow-lg w-full max-w-[500px] border border-[#2F3336]">
      {/* Post */}
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-sm">M</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <span className="font-bold text-[#E7E9EA] text-sm truncate">Mooncloak</span>
              <svg className="w-4 h-4 text-[#1D9BF0] flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"/>
              </svg>
              <span className="text-[#71767B] text-sm">@mooncloak · 2h</span>
            </div>

            {/* Promoted label */}
            <div className="text-xs text-[#71767B] mt-0.5">Promoted</div>
          </div>
          <button className="p-2 hover:bg-[#1D9BF0]/10 rounded-full">
            <MoreHorizontal className="w-5 h-5 text-[#71767B]" />
          </button>
        </div>

        {/* Tweet text */}
        <div className="mt-2 ml-13">
          <p className="text-[#E7E9EA] text-sm leading-relaxed">
            Your privacy is not for sale. Take back control of your data with Mooncloak. 🛡️
          </p>
        </div>

        {/* Ad image */}
        <div className="mt-3 ml-13 rounded-2xl overflow-hidden border border-[#2F3336]">
          {children}
        </div>

        {/* Action buttons */}
        <div className="mt-3 ml-13 flex items-center justify-between max-w-[425px]">
          <button className="flex items-center gap-2 text-[#71767B] hover:text-[#1D9BF0] group">
            <div className="p-2 group-hover:bg-[#1D9BF0]/10 rounded-full">
              <MessageCircle className="w-[18px] h-[18px]" />
            </div>
            <span className="text-sm">284</span>
          </button>
          <button className="flex items-center gap-2 text-[#71767B] hover:text-[#00BA7C] group">
            <div className="p-2 group-hover:bg-[#00BA7C]/10 rounded-full">
              <Repeat2 className="w-[18px] h-[18px]" />
            </div>
            <span className="text-sm">1.2K</span>
          </button>
          <button className="flex items-center gap-2 text-[#71767B] hover:text-[#F91880] group">
            <div className="p-2 group-hover:bg-[#F91880]/10 rounded-full">
              <Heart className="w-[18px] h-[18px]" />
            </div>
            <span className="text-sm">5.8K</span>
          </button>
          <button className="flex items-center gap-2 text-[#71767B] hover:text-[#1D9BF0] group">
            <div className="p-2 group-hover:bg-[#1D9BF0]/10 rounded-full">
              <BarChart2 className="w-[18px] h-[18px]" />
            </div>
            <span className="text-sm">42K</span>
          </button>
          <button className="text-[#71767B] hover:text-[#1D9BF0] group">
            <div className="p-2 group-hover:bg-[#1D9BF0]/10 rounded-full">
              <Share className="w-[18px] h-[18px]" />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
